import { useEffect, useRef, useState } from 'react';
import { CaretLeft, X } from '@phosphor-icons/react';
import { useNotification } from '../../../contexts/NotificationContext';
import { useViewport } from '../hooks';
import { istKompakt } from '../island-size';
import { FARBE } from '../pille-stil';
import { cn } from '../../../lib/cn';
import { useIslandState, useIslandActions, type DeckPage } from '../IslandProvider';
import { PAGE_META, SWIPE_THROTTLE_MS } from '../constants';
import { ControlsPage } from './ControlsPage';
import { SearchPage } from './SearchPage';
import { NotificationsPage } from './NotificationsPage';
import { ChatPage } from './ChatPage';
import { WidgetsPage } from './WidgetsPage';
import { ZeitPage } from './ZeitPage';
import { TelefonPage } from './TelefonPage';

const PAGES: DeckPage[] = ['controls', 'notifications', 'search', 'widgets'];

/** Seiten, die kein eigener Tab sind, sondern aus einer Tab-Seite heraus geöffnet werden.
 *  Die Kopfzeile zeigt dann Zurück-Pfeil + Titel statt der Tab-Leiste. */
const UNTERSEITEN: Partial<Record<DeckPage, { titel: string; zurueck: DeckPage }>> = {
  chat: { titel: 'Team Chat', zurueck: 'widgets' },
  zeit: { titel: 'Zeiterfassung', zurueck: 'widgets' },
  telefon: { titel: 'Telefon', zurueck: 'widgets' },
};

/** Deck — Header (Seiten-Tabs · Schließen) + nur die AKTIVE Seite.
 *  Blättern per Tab ODER Wisch-Geste (Trackpad/Touch), mit gerichtetem
 *  Ein-/Ausblenden. Kein horizontaler Strip → keine Seiten-Überlappung. */
export function Deck({ reducedMotion }: { reducedMotion: boolean }) {
  const { deckPage } = useIslandState();
  const actions = useIslandActions();
  const { unreadCount } = useNotification();
  const vp = useViewport();
  const mobil = istKompakt(vp.width, vp.height);

  const unterseite = UNTERSEITEN[deckPage];
  // Auf einer Unterseite bleibt der Tab hervorgehoben, aus dem sie geöffnet wurde.
  const activeIndex = Math.max(0, PAGES.indexOf(unterseite ? unterseite.zurueck : deckPage));
  const meta = PAGE_META[deckPage] ?? PAGE_META['notifications'];

  // Richtung des letzten Seitenwechsels (+1 vor, -1 zurück, 0 = neutral/Fade)
  const [dir, setDir] = useState(0);
  const areaRef = useRef<HTMLDivElement>(null);
  const lockRef = useRef(false);
  const lockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function goTo(i: number) {
    const clamped = Math.max(0, Math.min(i, PAGES.length - 1));
    if (clamped === activeIndex) return;
    setDir(Math.sign(clamped - activeIndex));
    actions.setDeckPage(PAGES[clamped]);
  }

  // Wisch-Geste: Trackpad-deltaX (non-passiv, damit preventDefault greift) +
  // Touch-Swipe. Eine Geste = eine Seite (Sperre via SWIPE_THROTTLE_MS).
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;

    const step = (forward: boolean) => {
      if (lockRef.current) return;
      if (UNTERSEITEN[deckPage]) return; // Unterseite: Wischen würde sie unbemerkt verlassen
      const target = Math.max(0, Math.min(activeIndex + (forward ? 1 : -1), PAGES.length - 1));
      if (target === activeIndex) return;
      lockRef.current = true;
      lockTimerRef.current = setTimeout(() => { lockRef.current = false; }, SWIPE_THROTTLE_MS);
      setDir(forward ? 1 : -1);
      actions.setDeckPage(PAGES[target]);
    };

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // nur horizontal-dominante Gesten
      e.preventDefault();
      if (Math.abs(e.deltaX) < 30) return;
      step(e.deltaX > 0);
    };

    let startX = 0;
    let startY = 0;
    let tracking = false;
    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      tracking = true;
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) step(dx < 0); // links wischen → vor
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [activeIndex, actions, deckPage]);

  // Sperr-Timer beim Unmount aufräumen (der Cleanup oben entfernt nur die Listener)
  useEffect(() => () => { if (lockTimerRef.current) clearTimeout(lockTimerRef.current); }, []);

  // Browser-Zurück/Vor (Two-Finger-Swipe) abschalten, solange das Deck offen ist.
  // Deck ist nur im deck-Mode gemountet → gilt ausschließlich während der Nutzung.
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.overscrollBehaviorX;
    root.style.overscrollBehaviorX = 'none';
    return () => { root.style.overscrollBehaviorX = prev; };
  }, []);

  const pageAnim = reducedMotion
    ? ''
    : dir > 0
      ? 'island-slide-r'
      : dir < 0
        ? 'island-slide-l'
        : 'island-fade';

  return (
    <div
      ref={areaRef}
      className="flex h-full flex-col"
      style={{ touchAction: 'pan-y', overscrollBehaviorX: 'none' }}
    >
      {/* Kopf (Handoff Pille §5): iOS-Tab-Leiste mittig, Schließen rechts; Unterseiten mit „‹ Titel“ in Gold */}
      <div className="relative flex shrink-0 items-center justify-center" style={{ height: 64, padding: unterseite ? '0 54px 0 8px' : '0 54px' }}>
        {unterseite ? (
          <button
            type="button"
            onClick={() => { setDir(-1); actions.setDeckPage(unterseite.zurueck); }}
            className="mr-auto flex cursor-pointer items-center"
            style={{ height: mobil ? 44 : 36, gap: 2, padding: '0 8px 0 2px', border: 'none', background: 'transparent', color: FARBE.gold, fontSize: 17, fontWeight: 400, fontFamily: 'inherit' }}
          >
            <CaretLeft size={20} weight="bold" className="shrink-0" />
            <span className="whitespace-nowrap">{unterseite.titel}</span>
          </button>
        ) : (
          <div role="tablist" aria-label="Bereiche der Pille" className="flex min-w-0 items-center"
            style={{ padding: 4, borderRadius: 26, gap: 2, background: 'rgba(118,118,128,0.24)' }}>
            {PAGES.map((p, i) => {
              const aktiv = i === activeIndex;
              const TabIcon = PAGE_META[p].icon;
              const badge = p === 'notifications' && unreadCount > 0 && !aktiv;
              return (
                <button
                  key={p}
                  type="button"
                  role="tab"
                  title={PAGE_META[p].label}
                  aria-label={PAGE_META[p].label}
                  aria-selected={aktiv}
                  onClick={() => goTo(i)}
                  className="relative flex shrink-0 cursor-pointer flex-col items-center justify-center"
                  style={{
                    minWidth: 58, height: 44, borderRadius: 22, padding: '0 10px', gap: 1, border: 'none', fontFamily: 'inherit',
                    background: aktiv ? 'rgba(255,255,255,0.14)' : 'transparent',
                    color: aktiv ? FARBE.gold : FARBE.text2,
                    boxShadow: aktiv ? 'inset 0 0.5px 0 rgba(255,255,255,0.25), 0 4px 12px -4px rgba(0,0,0,0.4)' : 'none',
                    transition: 'background .3s, color .3s',
                  }}
                >
                  <TabIcon size={19} weight={aktiv ? 'fill' : 'regular'} />
                  <span style={{ fontSize: 10, fontWeight: aktiv ? 700 : 500 }}>{PAGE_META[p].kurz}</span>
                  {badge && (
                    <span className="absolute tabular-nums" aria-label={`${unreadCount} ungelesen`}
                      style={{ top: 3, left: 'calc(50% + 5px)', minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, fontSize: 10, fontWeight: 700, lineHeight: '16px', background: '#ff3b30', color: '#fff', boxShadow: '0 0 0 2px #1c1c1e' }}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <button
          type="button"
          onClick={() => actions.collapse()}
          aria-label="Schließen"
          className="absolute flex cursor-pointer items-center justify-center rounded-full"
          style={{ right: mobil ? 6 : 12, top: mobil ? 10 : 17, width: mobil ? 44 : 30, height: mobil ? 44 : 30, border: 'none', background: mobil ? 'transparent' : 'rgba(118,118,128,0.24)', color: FARBE.text2 }}
        >
          <span className="flex items-center justify-center rounded-full" style={{ width: 30, height: 30, background: mobil ? 'rgba(118,118,128,0.24)' : 'transparent' }}>
            <X size={14} weight="bold" />
          </span>
        </button>
      </div>

      {/* Nur die AKTIVE Seite rendern — kein horizontaler Strip, daher physisch
          unmöglich, dass zwei Seiten überlappen. Wischen blättert (s. oben),
          neue Seite gleitet gerichtet herein (pageAnim). */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div
          key={deckPage}
          className={cn('h-full w-full', pageAnim)}
        >
          {deckPage === 'controls' ? (
            <ControlsPage />
          ) : deckPage === 'search' ? (
            <SearchPage active />
          ) : deckPage === 'notifications' ? (
            <NotificationsPage />
          ) : deckPage === 'chat' ? (
            <ChatPage />
          ) : deckPage === 'zeit' ? (
            <ZeitPage />
          ) : deckPage === 'telefon' ? (
            <TelefonPage />
          ) : deckPage === 'widgets' ? (
            <WidgetsPage />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
              <span className="text-sm font-medium text-white/70">{meta.label}</span>
              <span className="text-xs text-white/30">kommt in Kürze</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
